/**
 * Pagina /superadmin/personas — Sprint 13 S13-12.
 *
 * Mantenedor del super_admin para la carga inicial de propietarios y
 * vecinos del barrio (LIVE). Cumple doctrina Portal vs Superadmin §4.2.
 *
 * UI: listado con search por nombre/RUT/email + boton "+ Nueva persona"
 * + "Editar" por fila. RUT no se puede editar despues de crear.
 *
 * RBAC: require_super_admin (cuenta institucional administracion@).
 *
 * Nota: la pagina /superadmin/roles ya consume `listPersonas` con
 * roles vigentes + lote. Esta vista es CRUD puro, sin roles ni
 * tenencias — esos viven en pantallas separadas (S13-13 para
 * tenencias, S12-05 para roles).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { InputRut } from '../../components/ui/InputRut';
import { InputFecha } from '../../components/ui/InputFecha';
import {
  formatRut,
  formatearRutEnVivo,
  validarRutChileno,
} from '../../lib/format';
import {
  actualizarPersona,
  crearPersona,
  eliminarPersona,
  importPersonasCsv,
  listPersonas,
} from '../../lib/api/personas';
import type {
  PersonaConRoles,
  PersonaCreate,
  PersonaUpdate,
} from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { ActionButton } from '../../components/ui/ActionButton';
import {
  Table,
  TBody,
  THead,
  Th,
  Tr,
  Td,
} from '../../components/ui/DataTable';
import { PageHeader } from '../../components/layout/PageHeader';
import { ImportCsvSection } from '../../components/ImportCsvSection';
import type { CsvColumna } from '../../components/ImportCsvSection';

/**
 * Formatea un celular chileno como `9.XXXX.XXXX` para display.
 * Se guarda en DB sin puntos. Si la entrada no calza con el patron
 * esperado, se devuelve tal cual.
 */
function formatCelular(raw: string | null | undefined): string {
  if (!raw) return '—';
  // Limpia puntos, espacios y guiones para normalizar.
  const limpio = raw.replace(/[.\s-]/g, '');
  // Quita prefijo +56 si viene.
  const sinPrefijo = limpio.startsWith('+56')
    ? limpio.slice(3)
    : limpio.startsWith('56') && limpio.length > 9
      ? limpio.slice(2)
      : limpio;
  if (/^9\d{8}$/.test(sinPrefijo)) {
    return `${sinPrefijo[0]}.${sinPrefijo.slice(1, 5)}.${sinPrefijo.slice(5)}`;
  }
  return raw;
}

const PERSONAS_CSV_COLUMNAS: CsvColumna[] = [
  {
    nombre: 'nombre_completo',
    obligatoria: true,
    ejemplo: 'Juan Pérez González',
  },
  {
    nombre: 'rut',
    obligatoria: true,
    ejemplo: '12345678-9',
    notas: 'Identifica a la persona. No editable después.',
  },
  {
    nombre: 'email',
    obligatoria: false,
    ejemplo: 'juan@example.cl',
    notas: 'Puede estar vacío.',
  },
  {
    nombre: 'celular',
    obligatoria: false,
    ejemplo: '+56912345678',
    notas: 'Puede estar vacío.',
  },
  {
    nombre: 'fecha_nacimiento',
    obligatoria: false,
    ejemplo: '1985-03-15',
    notas: 'Formato YYYY-MM-DD. Puede estar vacía.',
  },
];

interface ModalCrearProps {
  onCerrar: () => void;
  onCreado: () => void;
}

function ModalCrear({ onCerrar, onCreado }: ModalCrearProps) {
  const [nombre, setNombre] = useState('');
  const [rut, setRut] = useState('');
  const [email, setEmail] = useState('');
  const [celular, setCelular] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sprint 13 S13-17: RUT opcional al crear.
  const valido = nombre.trim().length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    // Doctrina RUT: valida mod11 en el cliente y envía canónico (sin puntos).
    let rutCanonico: string | null = null;
    if (rut.trim()) {
      const v = validarRutChileno(rut);
      if (!v.valid) {
        setError('El RUT no es válido (revisa el dígito verificador).');
        return;
      }
      rutCanonico = v.canonico;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: PersonaCreate = {
        nombre_completo: nombre.trim(),
        rut: rutCanonico,
        email: email.trim() || null,
        celular: celular.trim() || null,
        fecha_nacimiento: fechaNacimiento || null,
      };
      await crearPersona(payload);
      onCreado();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Nueva persona"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Nueva persona
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Nombre completo</span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Juan Pérez González"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
            maxLength={200}
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">RUT (opcional)</span>
          <InputRut value={rut} onChange={(f) => setRut(f)} requerido={false} />
          <span className="text-xs text-text-muted">
            Si no lo tienes ahora, se puede agregar después editando la persona.
          </span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Email (opcional)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              maxLength={255}
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Celular (opcional)</span>
            <input
              type="text"
              value={celular}
              onChange={(e) => setCelular(e.target.value)}
              placeholder="+56912345678"
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              maxLength={30}
            />
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Fecha de nacimiento (opcional)
          </span>
          <InputFecha
            value={fechaNacimiento}
            onChange={setFechaNacimiento}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Creando…' : 'Crear persona'}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface ModalEditarProps {
  persona: PersonaConRoles;
  onCerrar: () => void;
  onActualizado: () => void;
}

function ModalEditar({
  persona,
  onCerrar,
  onActualizado,
}: ModalEditarProps) {
  const [nombre, setNombre] = useState(persona.nombre_completo);
  const [rut, setRut] = useState(formatearRutEnVivo(persona.rut ?? ''));
  const [email, setEmail] = useState(persona.email ?? '');
  const [celular, setCelular] = useState(persona.celular ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    // Doctrina RUT: valida mod11 y envía canónico (sin puntos).
    let rutCanonico: string | null = null;
    if (rut.trim()) {
      const v = validarRutChileno(rut);
      if (!v.valid) {
        setError('El RUT no es válido (revisa el dígito verificador).');
        return;
      }
      rutCanonico = v.canonico;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: PersonaUpdate = {
        nombre_completo: nombre.trim() || undefined,
        rut: rutCanonico,
        email: email.trim() || null,
        celular: celular.trim() || null,
      };
      await actualizarPersona(persona.id, payload);
      onActualizado();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={`Editar persona ${persona.nombre_completo}`}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Editar persona
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Nombre completo</span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            maxLength={200}
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">RUT</span>
          <InputRut value={rut} onChange={(f) => setRut(f)} requerido={false} />
          <span className="text-xs text-text-muted">
            Sprint 13 S13-17: editable. Vacío = sin RUT registrado.
          </span>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            maxLength={255}
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Celular</span>
          <input
            type="text"
            value={celular}
            onChange={(e) => setCelular(e.target.value)}
            placeholder="912345678"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            maxLength={30}
          />
          <span className="text-xs text-text-muted">
            Vacío = sin celular. Se guarda sin puntos; en el listado se
            muestra como 9.XXXX.XXXX.
          </span>
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function PersonasPage() {
  const [personas, setPersonas] = useState<PersonaConRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [editando, setEditando] = useState<PersonaConRoles | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [eliminandoId, setEliminandoId] = useState<string | null>(null);
  const [errorEliminar, setErrorEliminar] = useState<string | null>(null);
  // Sprint 17 Z22: modal de confirmación reemplaza window.confirm.
  const [personaAEliminar, setPersonaAEliminar] =
    useState<PersonaConRoles | null>(null);

  function handleEliminar(persona: PersonaConRoles) {
    if (eliminandoId) return;
    setErrorEliminar(null);
    setPersonaAEliminar(persona);
  }

  async function confirmarEliminar() {
    if (!personaAEliminar) return;
    setEliminandoId(personaAEliminar.id);
    setErrorEliminar(null);
    try {
      await eliminarPersona(personaAEliminar.id);
      setPersonaAEliminar(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorEliminar(detail);
      } else {
        setErrorEliminar('Error de red al eliminar');
      }
    } finally {
      setEliminandoId(null);
    }
  }

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listPersonas({ activo: true })
      .then((data) => {
        if (!cancelado) setPersonas(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar personas`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return personas;
    return personas.filter((p) => {
      const hay = `${p.nombre_completo} ${p.rut ?? ''} ${p.email ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [personas, search]);

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Mantenedor — Carga inicial"
        title="Personas"
        description="Carga inicial de propietarios, arrendatarios y otras personas. El RUT identifica a cada persona y no se puede editar después de creada. Para vincularla a una casa, usar el mantenedor de tenencias."
        action={
          <Button variant="primary" onClick={() => setMostrarCrear(true)}>
            + Nueva persona
          </Button>
        }
      />

      <ImportCsvSection
        titulo="Importar personas desde CSV"
        descripcion="Carga masiva de personas (útil para propietarios, arrendatarios, residentes del LIVE inicial). El RUT debe ser único; las filas con RUT duplicado se reportan como error y no se crean."
        columnas={PERSONAS_CSV_COLUMNAS}
        nombrePlantilla="plantilla-personas-calbucura.csv"
        subir={importPersonasCsv}
        onResultado={() => setRefreshKey((k) => k + 1)}
      />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, RUT o email…"
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary w-80"
        />
        <span className="text-xs text-text-muted">
          {filtradas.length} de {personas.length}
        </span>
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}
      {errorEliminar && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {errorEliminar}{' '}
          <button
            type="button"
            onClick={() => setErrorEliminar(null)}
            className="underline ml-2"
          >
            Cerrar
          </button>
        </div>
      )}
      {!loading && !error && filtradas.length === 0 && (
        <p className="text-sm text-text-muted">
          {personas.length === 0
            ? 'No hay personas registradas. Usa "+ Nueva persona" para empezar.'
            : 'No hay personas que coincidan con la búsqueda.'}
        </p>
      )}
      {!loading && !error && filtradas.length > 0 && (
        <Table>
          <THead>
            <Th>Nombre</Th>
            <Th>RUT</Th>
            <Th>Email</Th>
            <Th>Celular</Th>
            <Th>Casa</Th>
            <Th align="right">Acciones</Th>
          </THead>
          <TBody>
            {filtradas.map((p) => (
              <Tr key={p.id} data-testid="fila-persona">
                <Td tone="primary">{p.nombre_completo}</Td>
                <Td mono>{formatRut(p.rut) || '—'}</Td>
                <Td>{p.email ?? '—'}</Td>
                <Td mono>{formatCelular(p.celular)}</Td>
                <Td>
                  {p.lote_identificador
                    ? `${p.lote_identificador} (${p.lote_sector})`
                    : '—'}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    <ActionButton variant="edit" onClick={() => setEditando(p)}>
                      Editar
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() => handleEliminar(p)}
                      disabled={eliminandoId === p.id}
                    >
                      {eliminandoId === p.id ? 'Eliminando…' : 'Eliminar'}
                    </ActionButton>
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {mostrarCrear && (
        <ModalCrear
          onCerrar={() => setMostrarCrear(false)}
          onCreado={() => {
            setMostrarCrear(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {editando && (
        <ModalEditar
          persona={editando}
          onCerrar={() => setEditando(null)}
          onActualizado={() => {
            setEditando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* Sprint 17 Z22: modal de confirmación de eliminación */}
      {personaAEliminar && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-medium text-text-primary">
                Eliminar persona
              </h2>
              <button
                type="button"
                onClick={() => {
                  setPersonaAEliminar(null);
                  setErrorEliminar(null);
                }}
                className="text-text-muted hover:text-text-primary text-xl leading-none"
                aria-label="Cerrar"
                disabled={eliminandoId !== null}
              >
                ×
              </button>
            </div>
            <p className="text-sm text-text-secondary">
              ¿Eliminar a{' '}
              <span className="text-text-primary font-medium">
                {personaAEliminar.nombre_completo}
              </span>
              ? La acción borra Persona + Usuario + cuenta de portal en
              cascada. Si la persona tiene tenencias o residentes
              asociados, NO se elimina y debes usar el flujo ex-vecino.
              Esta operación NO es reversible.
            </p>
            {errorEliminar && (
              <div
                className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
                role="alert"
              >
                {errorEliminar}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setPersonaAEliminar(null);
                  setErrorEliminar(null);
                }}
                className="px-4 py-2 text-sm text-text-secondary hover:bg-bg-sunken rounded-sm disabled:opacity-50"
                disabled={eliminandoId !== null}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarEliminar}
                className="px-4 py-2 text-sm bg-state-danger hover:bg-state-danger/80 text-white font-medium rounded-sm disabled:opacity-50"
                disabled={eliminandoId !== null}
              >
                {eliminandoId !== null
                  ? 'Eliminando…'
                  : 'Sí, eliminar persona'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
